import {BookingTimeSlots, UnavailableDateRange} from "../../domain/types";
import {invertWeekdays, isWeekday, Weekday} from "../../domain/weekdays";
import {
  V2BookingDaysResponse,
  V2TimeSlotsResponse,
  V2UnavailableDatesResponse,
} from "./mapper.types";

/**
 * Maps v2 API payloads to booking-availability domain types.
 *
 * Pure TypeScript - no React imports.
 */
export const BookingAvailabilityMapperV2 = {
  /**
   * Maps `daysWithoutAvailability` to the bookable weekdays the calendar wants.
   *
   * Unrecognised weekday keys are ignored rather than dropping a day from the
   * calendar, so a typo or a new key from the API cannot silently hide
   * availability (Tolerant Reader).
   */
  toAvailableWeekdays(body: V2BookingDaysResponse): Weekday[] {
    const unavailable = (body?.data?.daysWithoutAvailability ?? []).filter(isWeekday);
    return invertWeekdays(unavailable);
  },

  /**
   * Maps the v2 unavailable-dates payload, dropping entries missing either bound
   * since a half-open range cannot be applied to the calendar grid.
   */
  toUnavailableDates(body: V2UnavailableDatesResponse): UnavailableDateRange[] {
    return (body?.data ?? []).reduce<UnavailableDateRange[]>((ranges, range) => {
      if (range?.from && range?.to) {
        ranges.push({from: range.from, to: range.to});
      }
      return ranges;
    }, []);
  },

  /**
   * Maps the v2 time-slots payload. Any status other than "available" is
   * normalized to "unavailable" so new server-side statuses cannot leak through
   * as bookable.
   */
  toTimeSlots(body: V2TimeSlotsResponse): BookingTimeSlots {
    const data = body?.data;

    return {
      date: data?.date ?? "",
      coachTimezone: data?.coachTimezone ?? "",
      customerTimezone: data?.customerTimezone ?? "",
      slots: (data?.slots ?? []).map((slot) => ({
        time: slot.time,
        status:
          slot.status === "available" ? ("available" as const) : ("unavailable" as const),
      })),
    };
  },
};
